// src/components/EventTrackingInitializer.jsx

import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useEventTrackingStore } from "../zustand/eventTrackingStore";
import useUserStore from "../zustand/UseUserStore";
import { SESSION_ID } from "../api/apiConfig";
import { eventTracker } from "../services/eventTracking";

/**
 * Component that initializes and manages event tracking
 * Integrates with GLOW's existing UseUserStore and SESSION_ID
 */
export const EventTrackingInitializer = () => {
	const { initialize, updateSession } = useEventTrackingStore();
	const location = useLocation();

	// Get user state from GLOW's existing Zustand store
	const user = useUserStore((state) => state.user);
	const isAuthenticated = useUserStore(
		(state) => state.userProgress.isAuthenticated
	);

	// Initialize on mount
	useEffect(() => {
		const initTracking = async () => {
			const currentSessionId = SESSION_ID();
			const memberId = user?.MemberID || null;

			await initialize(currentSessionId, memberId);
		};

		initTracking();
	}, []); // Run once on mount

	// Update session when user logs in/out
	useEffect(() => {
		const { isInitialized } = useEventTrackingStore.getState();

		// If tracking is not initialized (e.g., after logout), re-initialize it
		if (!isInitialized) {
			const initTracking = async () => {
				const currentSessionId = SESSION_ID();
				const memberId =
					isAuthenticated && user?.MemberID ? user.MemberID : null;
				await initialize(currentSessionId, memberId);
			};
			initTracking();
		} else if (isAuthenticated && user?.MemberID) {
			// User is authenticated, update session with member ID
			const currentSessionId = SESSION_ID();
			updateSession(currentSessionId, user.MemberID);
		} else if (!isAuthenticated) {
			// User logged out, update session with null member ID
			const currentSessionId = SESSION_ID();
			updateSession(currentSessionId, null);
		}
	}, [isAuthenticated, user?.MemberID, updateSession, initialize]);

	// Reset scroll depth and flush any failed events on route change
	useEffect(() => {
		// Flush any failed events that were queued (retry them)
		if (eventTracker.eventQueue.length > 0) {
			eventTracker.flush(false);
		}

		eventTracker.resetScrollDepth();
	}, [location.pathname]);

	// This component doesn't render anything
	return null;
};
